import React, { FC, useEffect, useRef, useState } from 'react';
import { Alert } from 'antd';
import {
  CaretRightFilled,
  CloseCircleFilled,
  CopyOutlined,
  ExclamationCircleFilled,
  ExpandOutlined,
  InfoCircleFilled,
  PauseOutlined
} from '@ant-design/icons';
import { getReplayConsolePlugin } from '@rrweb/rrweb-plugin-console-replay';
import Loader from '../../../javascript/components/loader';
import {
  consoleLogFrom,
  isParentPageConsoleTrace
} from '../parentPageConsole';
import 'rrweb-player/dist/style.css';
import styles from './styles.module.css';

type Props = {
  available?: boolean;
  apiDomain: string;
  projectId: number;
  taskId: number;
};

type Status = 'loading' | 'ready' | 'unavailable';

type ConsoleEntry = {
  level: string;
  message: string;
  source?: string;
  timestamp: number;
};

type Player = {
  $destroy: () => void;
  $set: (dimensions: { width: number; height: number }) => void;
  addEventListener: (
    name: string,
    handler: (event: { payload: any }) => void
  ) => void;
  getMetaData: () => { totalTime: number };
  goto: (time: number, play?: boolean) => void;
  pause: () => void;
  play: () => void;
  setSpeed: (speed: number) => void;
  toggleSkipInactive: () => void;
  triggerResize: () => void;
};

const SPEEDS = [1, 2, 4, 8];
const FILTERS = [
  { id: 'error', label: 'Errors' },
  { id: 'warn', label: 'Warnings' },
  { id: 'all', label: 'All' }
];

const dimensionsFor = (container: HTMLDivElement, events: any[]) => {
  const width = container.clientWidth || 640;
  const meta = events.find(event => event.type === 4)?.data;
  const recordedWidth = meta?.width || 16;
  const recordedHeight = meta?.height || 9;
  const height = Math.round((width * recordedHeight) / recordedWidth);

  return { width, height: Math.min(Math.max(height, 160), 420) };
};

const decodePayload = (value: string) => {
  try {
    const parsed = JSON.parse(value);
    return typeof parsed === 'string' ? parsed : JSON.stringify(parsed);
  } catch (_error) {
    return value;
  }
};

const readableMessage = (message: string) => {
  const cleaned = message.replace(/\s+/g, ' ').trim();
  if (!cleaned.startsWith("Couldn't record")) return cleaned;

  return cleaned.split(/:\s*</)[0];
};

const sourceFromTrace = (trace?: string[]) => {
  const line = trace?.find(item => item);
  const match = line?.match(/([\w.-]+\.js)(?:\?[^:)]*)?:(\d+)/);
  return match ? `${match[1]}:${match[2]}` : undefined;
};

const consoleEntriesFrom = (events: any[]): ConsoleEntry[] => {
  const parentHost = pageHostFrom(events);

  return events.flatMap(event => {
    const log = consoleLogFrom(event);

    if (!log?.level || !Array.isArray(log.payload)) return [];
    if (!isParentPageConsoleTrace(log.trace, parentHost)) return [];

    return [
      {
        level: log.level,
        message: readableMessage(log.payload.map(decodePayload).join(' ')),
        source: sourceFromTrace(log.trace),
        timestamp: event.timestamp
      }
    ];
  });
};

const formatTime = (time: number) => {
  const seconds = Math.max(0, Math.floor(time / 1000));
  return `${Math.floor(seconds / 60)}:${(seconds % 60)
    .toString()
    .padStart(2, '0')}`;
};

const filterLevel = (level: string) => (level === 'log' ? 'info' : level);

const pageHostFrom = (events: any[]) => {
  const href = events.find(event => event.type === 4)?.data?.href;
  if (!href) return '';

  try {
    return new URL(href).host;
  } catch (_error) {
    return '';
  }
};

const pageLabelFrom = (events: any[]) => {
  const href = events.find(event => event.type === 4)?.data?.href;
  if (!href) return '';

  try {
    const url = new URL(href);
    const path = url.pathname === '/' ? '' : url.pathname;
    return `${url.host}${path}`;
  } catch (_error) {
    return '';
  }
};

const levelIcon = (level: string) => {
  if (level === 'error') return <CloseCircleFilled />;
  if (level === 'warn') return <ExclamationCircleFilled />;
  return <InfoCircleFilled />;
};

export const SessionReplay: FC<Props> = ({
  available,
  apiDomain,
  projectId,
  taskId
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<Player | null>(null);
  const [status, setStatus] = useState<Status>('loading');
  const [consoleEntries, setConsoleEntries] = useState<ConsoleEntry[]>([]);
  const [startTime, setStartTime] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [skipInactive, setSkipInactive] = useState(true);
  const [speed, setSpeed] = useState(1);
  const [consoleFilter, setConsoleFilter] = useState('error');
  const [pageLabel, setPageLabel] = useState('');

  useEffect(() => {
    if (!available || !containerRef.current) return;

    let cancelled = false;
    let player: Player | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let playerWidth = 0;
    const container = containerRef.current;
    const abortController = new AbortController();

    const loadReplay = async () => {
      setStatus('loading');

      try {
        const response = await fetch(
          `${apiDomain}/projects/${projectId}/tasks/${taskId}/session_replay`,
          {
            credentials: 'include',
            headers: { Accept: 'application/json' },
            signal: abortController.signal
          }
        );

        if (!response.ok) throw new Error('Session replay is unavailable');

        const events = await response.json();
        if (!Array.isArray(events) || events.length === 0) {
          throw new Error('Session replay is unavailable');
        }

        const { default: RrwebPlayer } = await import('rrweb-player');
        if (cancelled) return;

        const createPlayer = () => {
          const dimensions = dimensionsFor(container, events);
          if (player && dimensions.width === playerWidth) return;
          playerWidth = dimensions.width;

          if (!player) {
            player = new RrwebPlayer({
              target: container,
              props: {
                events,
                ...dimensions,
                autoPlay: false,
                showController: false,
                skipInactive: true,
                mouseTail: false,
                plugins: [
                  getReplayConsolePlugin({
                    level: ['log', 'info', 'warn', 'error']
                  })
                ],
                maxScale: 1
              }
            });
            playerRef.current = player;
            setStartTime(events[0].timestamp || 0);
            setDuration(player.getMetaData().totalTime);
            player.addEventListener('ui-update-current-time', event => {
              if (!cancelled) setCurrentTime(event.payload);
            });
            player.addEventListener('ui-update-player-state', event => {
              if (!cancelled) setIsPlaying(event.payload === 'playing');
            });
          } else {
            player.$set(dimensions);
            player.triggerResize();
          }
        };

        createPlayer();
        if ('ResizeObserver' in window) {
          resizeObserver = new ResizeObserver(createPlayer);
          resizeObserver.observe(container);
        }
        setConsoleEntries(consoleEntriesFrom(events));
        setPageLabel(pageLabelFrom(events));
        setStatus('ready');
      } catch (error) {
        if (!cancelled && (error as Error).name !== 'AbortError') {
          setStatus('unavailable');
        }
      }
    };

    loadReplay();

    return () => {
      cancelled = true;
      abortController.abort();
      if (resizeObserver) resizeObserver.disconnect();
      if (player) player.$destroy();
      playerRef.current = null;
      container.replaceChildren();
    };
  }, [available, apiDomain, projectId, taskId]);

  const filteredConsoleEntries = consoleEntries.filter(
    entry =>
      consoleFilter === 'all' || filterLevel(entry.level) === consoleFilter
  );
  const counts = consoleEntries.reduce<Record<string, number>>(
    (result, entry) => {
      const level = filterLevel(entry.level);
      return { ...result, [level]: (result[level] || 0) + 1 };
    },
    {}
  );

  const seek = (time: number) => {
    const nextTime = Math.max(0, Math.min(time, duration));
    playerRef.current?.goto(nextTime, isPlaying);
    setCurrentTime(nextTime);
  };

  const togglePlayback = () => {
    if (!playerRef.current) return;

    if (isPlaying) playerRef.current.pause();
    else playerRef.current.play();
    setIsPlaying(!isPlaying);
  };

  const cycleSpeed = () => {
    const nextSpeed = SPEEDS[(SPEEDS.indexOf(speed) + 1) % SPEEDS.length];
    playerRef.current?.setSpeed(nextSpeed);
    setSpeed(nextSpeed);
  };

  if (!available) return null;

  return (
    <section
      className={styles.wrapper}
      aria-label="Session replay"
      data-cy="session-replay"
    >
      <div className={styles.meta}>
        <div className={styles.title}>Session replay</div>
        {pageLabel && <div className={styles.page}>{pageLabel}</div>}
      </div>
      {status === 'loading' && (
        <div className={styles.loading} role="status">
          <Loader size="default" />
          <span>Loading replay...</span>
        </div>
      )}
      {status === 'unavailable' && (
        <Alert
          className={styles.unavailable}
          type="info"
          showIcon
          message="This session replay is no longer available."
        />
      )}
      <div className={styles.stage}>
        <div ref={containerRef} className={styles.player} />
        {status === 'ready' && (
          <>
            <input
              aria-label="Replay progress"
              className={styles.timeline}
              max={duration}
              min={0}
              onChange={event => seek(Number(event.target.value))}
              style={{
                background: `linear-gradient(to right, var(--primaryColor, #3b6ff5) ${
                  duration ? (currentTime / duration) * 100 : 0
                }%, #e6e8ee 0)`
              }}
              type="range"
              value={Math.min(currentTime, duration)}
            />
            <div className={styles.controls}>
              <button
                aria-label={isPlaying ? 'Pause replay' : 'Play replay'}
                className={styles.playButton}
                onClick={togglePlayback}
                type="button"
              >
                {isPlaying ? <PauseOutlined /> : <CaretRightFilled />}
              </button>
              <span className={styles.time}>
                {formatTime(currentTime)} /<span>{formatTime(duration)}</span>
              </span>
              <span className={styles.spacer} />
              <button
                aria-checked={skipInactive}
                className={styles.skipIdle}
                onClick={() => {
                  playerRef.current?.toggleSkipInactive();
                  setSkipInactive(!skipInactive);
                }}
                role="switch"
                type="button"
              >
                <span
                  className={skipInactive ? styles.switchOn : styles.switch}
                />
                Skip idle
              </button>
              <button
                aria-label="Playback speed"
                className={styles.speed}
                onClick={cycleSpeed}
                type="button"
              >
                {speed}x
              </button>
              <button
                aria-label="Expand replay"
                className={styles.expand}
                onClick={() => containerRef.current?.requestFullscreen()}
                type="button"
              >
                <ExpandOutlined />
              </button>
            </div>
          </>
        )}
      </div>
      {consoleEntries.length > 0 && (
        <section
          className={styles.console}
          aria-label="Recorded console output"
        >
          <div className={styles.consoleHeader}>
            <div className={styles.filters}>
              {FILTERS.map(filter => (
                <button
                  className={
                    consoleFilter === filter.id
                      ? styles.filterActive
                      : styles.filter
                  }
                  key={filter.id}
                  onClick={() => setConsoleFilter(filter.id)}
                  type="button"
                >
                  {filter.label}{' '}
                  {filter.id === 'all'
                    ? consoleEntries.length
                    : counts[filter.id] || 0}
                </button>
              ))}
            </div>
            <button
              className={styles.copyButton}
              onClick={() =>
                navigator.clipboard?.writeText(
                  filteredConsoleEntries
                    .map(entry => `[${entry.level}] ${entry.message}`)
                    .join('\n')
                )
              }
              type="button"
            >
              <CopyOutlined /> Copy logs
            </button>
          </div>
          <div className={styles.rows}>
            {filteredConsoleEntries.map((entry, index) => (
              <button
                className={`${styles.row} ${styles[entry.level] || styles.log}`}
                key={`${entry.timestamp}-${index}`}
                onClick={() => seek(entry.timestamp - startTime)}
                type="button"
              >
                <span className={styles.levelIcon}>
                  {levelIcon(entry.level)}
                </span>
                <span className={styles.messageBlock}>
                  <span className={styles.message}>{entry.message}</span>
                  {entry.source && (
                    <span className={styles.source}>{entry.source}</span>
                  )}
                </span>
                <span className={styles.seekTime}>
                  <CaretRightFilled />
                  {formatTime(entry.timestamp - startTime)}
                </span>
              </button>
            ))}
          </div>
        </section>
      )}
    </section>
  );
};

export default SessionReplay;
